import { NotificationService } from './../../../services/notification.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@bling-fe/shared/utils';

export type SupportedMimeTypes = 'pdf' | 'xls' | 'doc';

/** Limit of the base64 upload path, which posts the file inside the request. */
export const DEFAULT_MAX_SIZE_MB = 10;

export const mimeTypes: Record<string, SupportedMimeTypes> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xls',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'doc',
  'application/msword': 'doc',
  'application/x-ole-storage': 'doc'
};

@Component({
  selector: 'fl-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FileComponent {
  /** Comma separated concrete MIME types a picked file is checked against. */
  @Input() validFileTypes = Object.keys(mimeTypes).join(',');
  /**
   * `accept` for the native picker. Defaults to the checked MIME types, but may
   * be more permissive (eg. `video/*`) to keep the camera and recorder intents.
   */
  @Input() accept?: string;
  /**
   * Comma separated extensions a picked file is checked against, and when set
   * the only thing that decides. Ticket media needs it: the API enforces
   * extensions, while the browser reports `.m4v` as `video/x-m4v` on Apple
   * platforms and as `video/mp4` elsewhere.
   */
  @Input() validExtensions?: string;
  @Input() maxSizeMb = DEFAULT_MAX_SIZE_MB;
  /**
   * Copy for a file over `maxSizeMb`. The default names 10 MB, so inputs with
   * another limit have to pass the matching key.
   */
  @Input() sizeErrorKey: string = _('ERROR.WRONG_FILE_SIZE');
  /**
   * Copy for a file the input refuses. The default names no format at all, so
   * a single-kind picker passes the key that names the formats it takes.
   */
  @Input() formatErrorKey: string = _('ERROR.WRONG_FILE_FORMAT');
  /**
   * Parameters of the format error, on top of the refused file's `name`. A
   * picker for several kinds passes the formats it takes, so the toast can
   * name them.
   */
  @Input() formatErrorParams: Record<string, string> = {};
  @Input() enableDropZone = false;
  /**
   * Whether the drop zone shows its own "drop files here" line. A consumer
   * that projects its own copy into the zone turns it off.
   */
  @Input() showDropText = true;
  @Output() fileAdd: EventEmitter<File> = new EventEmitter();
  @ViewChild('fileInput') fileInput;

  @HostBinding('class.drag-over') isDragOver = false;

  constructor(
    private toast: ToastController,
    private translate: TranslateService,
    private notification: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    if (!this.enableDropZone) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
    this.cdr.markForCheck();
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    if (!this.enableDropZone) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.cdr.markForCheck();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    if (!this.enableDropZone) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.cdr.markForCheck();
    const files: File[] = Array.from(event.dataTransfer?.files || []);
    files.forEach(file => this.validateAndEmit(file));
  }

  fileChanged($event) {
    const files: File[] = Array.from($event.target.files);
    files.forEach(file => this.validateAndEmit(file));
    $event.target.value = null;
  }

  public openUpload() {
    this.fileInput.nativeElement.click();
  }

  /**
   * Public so a picker that had to live outside this component - the ticket
   * drop area takes every kind at once and the capture menu opens its own
   * inputs - can hand the files it collected to the same checks.
   */
  public validateAndEmit(file: File) {
    const fileSize = file.size / 1048576;
    const typeAccepted = this.isAcceptedType(file);

    if (typeAccepted && fileSize <= this.maxSizeMb) {
      this.fileAdd.emit(file);
    } else {
      if (fileSize > this.maxSizeMb) {
        this.notification.error(this.sizeErrorKey, {
          name: file.name,
          size: `${fileSize.toFixed(1)} MB`
        });
      }
      if (!typeAccepted) {
        this.notification.error(this.formatErrorKey, {
          name: file.name,
          ...this.formatErrorParams
        });
      }
    }
  }

  /**
   * A picker checked by extension signs with the extension's type, so it may
   * take a file the browser reports without one. A picker checked by MIME type
   * refuses it: the upload would dead-end on a request the API cannot sign.
   *
   * Whole MIME strings only. A substring test on the comma separated list lets
   * every file the browser reports without a type through.
   */
  private isAcceptedType(file: File): boolean {
    if (this.validExtensions) {
      return entriesOf(this.validExtensions).includes(extensionOf(file.name));
    }

    if (!file.type) {
      return false;
    }

    return entriesOf(this.validFileTypes).includes(file.type);
  }
}

const entriesOf = (list: string): string[] =>
  list.split(',').map(entry => entry.trim().toLowerCase());

export const extensionOf = (filename: string): string => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};
